import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/common/Button";
import { ConfirmActionModal } from "../ConfirmActionModal";
import type { RoomInspectorProps } from "./types";

export const RoomInspector = ({ state }: RoomInspectorProps) => {
  const { t } = useTranslation();
  const { room, isShapeChanged, isSaving, save, cancel, confirmDelete } = state;

  const [editedName, setEditedName] = useState(room.name);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const trimmedName = editedName.trim();
  const isDirty = trimmedName !== room.name || isShapeChanged;

  const handleConfirmDelete = async () => {
    await confirmDelete();
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-2 p-3 rounded-md border border-blue-400 bg-white">
      <input
        type="text"
        value={editedName}
        placeholder={t("smartSurveyor.rooms.namePlaceholder")}
        aria-label={t("smartSurveyor.rooms.nameLabel")}
        maxLength={120}
        autoFocus
        onChange={(event) => setEditedName(event.target.value)}
        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 outline-none focus:border-blue-500"
      />
      <p className="text-xs text-gray-500">
        {t("smartSurveyor.rooms.editHint")}
      </p>
      <div className="flex gap-2">
        <Button className="flex-1" onClick={cancel} disabled={isSaving}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="danger"
          className="flex-1"
          onClick={() => setIsDeleteModalOpen(true)}
          disabled={isSaving}
        >
          {t("common.delete")}
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={() => save({ name: trimmedName })}
          disabled={!isDirty || trimmedName === "" || isSaving}
        >
          {isSaving ? t("smartSurveyor.rooms.saving") : t("common.save")}
        </Button>
      </div>
      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        action="delete"
        title={t("smartSurveyor.rooms.deleteModal.title")}
        description={t("smartSurveyor.rooms.deleteModal.description")}
      />
    </div>
  );
};
